import React, { useState, useRef, useEffect } from 'react';
import './DecorationSection.css';

const DecorationSection = () => {
  const [carouselStates, setCarouselStates] = useState({
    birthday: 0,
    kids: 0,
    anniversary: 0,
    babyShower: 0
  });

  const [loadedImages, setLoadedImages] = useState(new Set());
  const imageRefs = useRef({});

  const carouselRefs = {
    birthday: useRef(null),
    kids: useRef(null),
    anniversary: useRef(null),
    babyShower: useRef(null)
  };

  useEffect(() => {
    // Intersection Observer for lazy loading decoration images
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imgSrc = entry.target.dataset.src;
            if (imgSrc && !loadedImages.has(imgSrc)) {
              const img = new Image();
              img.src = imgSrc;
              img.onload = () => {
                setLoadedImages((prev) => new Set([...prev, imgSrc]));
                entry.target.style.backgroundImage = `url(${imgSrc})`;
                entry.target.classList.add('loaded');
              };
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: '100px' }
    );

    Object.values(imageRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(imageRefs.current).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [loadedImages]);

  const decorations = {
    birthday: [
      {
        id: 1,
        title: 'Birthday Decorations',
        image: '/images/BirthdayImages/freepik__35mm-film-photography-luxury-indian-birthday-decor__13647.webp',
        description: 'Elegant birthday setups'
      },
      {
        id: 2,
        title: 'Birthday Decorations',
        image: '/images/BirthdayImages/freepik__35mm-film-photography-premium-birthday-decoration-__13643.webp',
        description: 'Colorful celebrations'
      },
      {
        id: 3,
        title: 'Birthday Decorations',
        image: '/images/BirthdayImages/freepik__35mm-film-photography-premium-home-garden-birthday__13646.webp',
        description: 'Themed parties'
      },
      {
        id: 4,
        title: 'Birthday Decorations',
        image: '/images/BirthdayImages/freepik__the-style-is-candid-image-photography-with-natural__13644.webp',
        description: 'Premium arrangements'
      },
      {
        id: 5,
        title: 'Birthday Decorations',
        image: '/images/BirthdayImages/freepik__the-style-is-candid-image-photography-with-natural__13645.webp',
        description: 'Luxury celebrations'
      }
    ],
    kids: [
      {
        id: 1,
        title: "Kid's Birthday Decorations",
         image: '/images/KidBirthdayDecorations/Gemini_Generated_Image_kap93gkap93gkap9.webp',
        description: 'Fun Birthday Setup'
      },
      {
        id: 2,
        title: "Kid's Birthday Decorations",
        image: '/images/KidBirthdayDecorations/freepik__35mm-film-photography-premium-marvel-superheroes-t__76739.webp',
        description: 'Marvel Superheroes Theme'
      },
      {
        id: 3,
        title: "Kid's Birthday Decorations",
        image: '/images/KidBirthdayDecorations/freepik__the-style-is-candid-image-photography-with-natural__76738.webp',
        description: 'Natural Style Decorations'
      },
      {
        id: 4,
        title: "Kid's Birthday Decorations",
        image: '/images/KidBirthdayDecorations/Gemini_Generated_Image_dnendnendnendnen.webp',
        description: 'Colorful Kid Theme'
      },
      {
        id: 5,
        title: "Kid's Birthday Decorations",
        image: '/images/KidBirthdayDecorations/freepik__35mm-film-photography-organic-balloon-garland-circ__76740.webp',
        description: 'Organic Balloon Garland'
      }
    ],
    anniversary: [
      {
        id: 1,
        title: 'Anniversary Decorations',
        image: '/images/AnniversaryImages/Gemini_Generated_Image_wdkjmtwdkjmtwdkj.webp',
        description: 'Romantic Anniversary Setup'
      },
      {
        id: 2,
        title: 'Anniversary Decorations',
        image: '/images/AnniversaryImages/Gemini_Generated_Image_yx4r2uyx4r2uyx4r.webp',
        description: 'Elegant Anniversary Arrangements'
      },
      {
        id: 3,
        title: 'Anniversary Decorations',
        image: '/images/AnniversaryImages/Gemini_Generated_Image_p740z7p740z7p740.webp',
        description: 'Premium Anniversary Decor'
      },
      {
        id: 4,
        title: 'Anniversary Decorations',
        image: '/images/AnniversaryImages/Gemini_Generated_Image_4nhb0q4nhb0q4nhb.webp',
        description: '25th Anniversary Special'
      },
      {
        id: 5,
        title: 'Anniversary Decorations',
        image: '/images/AnniversaryImages/Gemini_Generated_Image_bl2lg6bl2lg6bl2l.webp',
        description: 'Golden Anniversary Celebration'
      }
    ],
    babyShower: [
      {
        id: 1,
        title: 'Baby Shower & Welcome',
        image: '/images/BabyShower/Gemini_Generated_Image_2e5abf2e5abf2e5a.webp',
        description: 'Welcome Baby Setups'
      },
      {
        id: 2,
        title: 'Baby Shower & Welcome',
        image: '/images/BabyShower/Gemini_Generated_Image_39tdbz39tdbz39td.webp',
        description: 'Elegant Baby Shower Celebrations'
      },
      {
        id: 3,
        title: 'Baby Shower & Welcome',
        image: '/images/BabyShower/Gemini_Generated_Image_j7otzmj7otzmj7ot.webp',
        description: 'Themed Baby Shower Decorations'
      },
      {
        id: 4,
        title: 'Baby Shower & Welcome',
        image: '/images/BabyShower/Gemini_Generated_Image_t2qxk9t2qxk9t2qx.webp',
        description: 'Premium Baby Shower Arrangements'
      },
      {
        id: 5,
        title: 'Baby Shower & Welcome',
        image: '/images/BabyShower/Gemini_Generated_Image_tkkg83tkkg83tkkg.webp',
        description: 'Luxury Baby Welcome Decor'
      }
    ]
  };

  const cardsPerView = 4;
  const getTotalPages = (items) => Math.ceil(items.length / cardsPerView);

  const scrollCarousel = (category, direction) => {
    const container = carouselRefs[category].current;
    if (!container) return;

    const cardWidth = container.querySelector('.decoration-card')?.offsetWidth || 0;
    const gap = 32; // 2rem gap
    const scrollAmount = (cardWidth + gap) * cardsPerView;

    if (direction === 'next') {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setCarouselStates(prev => ({
        ...prev,
        [category]: Math.min(prev[category] + 1, getTotalPages(decorations[category]) - 1)
      }));
    } else {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setCarouselStates(prev => ({
        ...prev,
        [category]: Math.max(prev[category] - 1, 0)
      }));
    }
  };

  const goToPage = (category, pageIndex) => {
    const container = carouselRefs[category].current;
    if (!container) return;

    const cardWidth = container.querySelector('.decoration-card')?.offsetWidth || 0;
    const gap = 32;
    const scrollAmount = (cardWidth + gap) * cardsPerView * pageIndex;

    container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    setCarouselStates(prev => ({
      ...prev,
      [category]: pageIndex
    }));
  };

  const handleScroll = (category) => {
    const container = carouselRefs[category].current;
    if (!container) return;

    const cardWidth = container.querySelector('.decoration-card')?.offsetWidth || 0;
    const gap = 32;
    const scrollLeft = container.scrollLeft;
    const currentPage = Math.round(scrollLeft / ((cardWidth + gap) * cardsPerView));

    setCarouselStates(prev => ({
      ...prev,
      [category]: Math.min(currentPage, getTotalPages(decorations[category]) - 1)
    }));
  };

  const renderCards = (items, category) => {
    return items.map((item, index) => {
      const imageKey = `${category}-${item.id}`;
      return (
        <div key={item.id} className="decoration-card" style={{ animationDelay: `${index * 0.1}s` }}>
          <div className="card-image-container">
            <div className="luxury-badge">Book My Decoration</div>
            <div 
              ref={(el) => (imageRefs.current[imageKey] = el)}
              className="card-image"
              data-src={item.image}
              style={loadedImages.has(item.image) ? { backgroundImage: `url(${item.image})` } : {}}
            >
              <div className="card-overlay">
                <a href="https://wa.me/9166007718" className="whatsapp-btn" target="_blank" rel="noopener noreferrer">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </a>
              </div>
            </div>
        </div>
        <div className="card-content">
          <h3 className="card-title">{item.title}</h3>
          <p className="card-description">{item.description}</p>
          <div className="card-actions">
            <a href="https://wa.me/9166007718" className="get-quotes-btn" target="_blank" rel="noopener noreferrer">Get Quotes</a>
          </div>
        </div>
      </div>
      );
    });
  };

  const renderCarousel = (category, title) => {
    const items = decorations[category];
    const totalPages = getTotalPages(items);
    const currentPage = carouselStates[category];

    return (
      <div className="category-section">
        <h2 className="section-title">{title}</h2>
        <div className="carousel-wrapper">
          <button 
            className="carousel-nav-btn prev-btn"
            onClick={() => scrollCarousel(category, 'prev')}
            disabled={currentPage === 0}
            aria-label="Previous"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <div 
            className="cards-carousel"
            ref={carouselRefs[category]}
            onScroll={() => handleScroll(category)}
          >
            {renderCards(items, category)}
          </div>
          <button 
            className="carousel-nav-btn next-btn"
            onClick={() => scrollCarousel(category, 'next')}
            disabled={currentPage >= totalPages - 1}
            aria-label="Next"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
        <div className="pagination-dots">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={`dot ${i === currentPage ? 'active' : ''}`}
              onClick={() => goToPage(category, i)}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="decorations" className="decoration-section">
      <div className="section-container">
        {renderCarousel('birthday', 'Birthday Decorations')}
        {renderCarousel('kids', "Kid's Birthday Decorations")}
        {renderCarousel('anniversary', 'Anniversary Decorations')}
        {renderCarousel('babyShower', 'Baby Shower & Welcome')}
      </div>
    </section>
  );
};

export default DecorationSection;
